/**
 * features/activities/types — tipos do domínio de atividades (TASK-14 + TASK-29).
 *
 * Mapeamento de enums do backend → portal:
 *
 * ActivityType (backend) → ActivityType (portal):
 *   "meeting"   → "meeting"
 *   "follow_up" → "follow_up"  (backend usa snake_case canônico)
 *   "call"      → "call"
 *   "email"     → "email"
 *   "task"      → "task"
 *
 * ActivityStatus (backend) → ActivityState (portal):
 *   "pending"     → derivado de dueAt (ver mapActivityState)
 *   "in_progress" → derivado de dueAt (ver mapActivityState)
 *   "completed"   → "done"
 *   "cancelled"   → "done"   (terminal sem completedAt — tratamos como encerrada)
 *   "missed"      → "done"   (terminal — compromisso perdido)
 *   "archived"    → "done"   (terminal — eliminado na revisão de encalhada)
 *
 *   Para status não-terminais, o estado visual é derivado de dueAt vs. agora:
 *     completedAt presente → "done"
 *     dueAt < hoje         → "overdue"
 *     dueAt === hoje       → "due"
 *     dueAt > hoje         → "upcoming"
 *
 * Priority (backend) → ActivityPriority (portal):
 *   "high"   → "high"
 *   "medium" → "med"   (backend usa "medium"; portal usa abreviação "med")
 *   "low"    → "low"
 *
 * ActivityNature (TASK-29):
 *   "task"        → agendado apenas com data (due_date)
 *   "appointment" → agendado com data+hora (scheduled_at)
 *
 * ActivityQuadrant (TASK-29) — Matriz de Eisenhower:
 *   Q1 = urgente + importante     → priority high
 *   Q2 = não urgente + importante → priority medium
 *   Q3 = urgente + não importante → priority medium
 *   Q4 = não urgente + não importante → priority low
 */

export type ActivityType = "call" | "email" | "meeting" | "task" | "follow_up";
export type ActivityState = "overdue" | "due" | "upcoming" | "done";
export type ActivityPriority = "high" | "med" | "low";

/** Status canônico do backend (string bruta — não derivado). */
export type ActivityStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "missed"
  | "archived";

/**
 * Natureza da atividade (TASK-29).
 * Derivada do agendamento: só data → task; data+hora → appointment.
 * Não editável diretamente — resulta da escolha de data vs. data+hora no form.
 */
export type ActivityNature = "task" | "appointment";

/**
 * Quadrante de Eisenhower (TASK-29).
 * Derivado de isUrgent × isImportant no backend — não editável no frontend.
 * Q1 = urgente+importante, Q2 = !urgente+importante,
 * Q3 = urgente+!importante, Q4 = !urgente+!importante.
 */
export type ActivityQuadrant = "Q1" | "Q2" | "Q3" | "Q4";

/**
 * Ação possível na revisão de uma atividade encalhada (TASK-29 / REQ-12).
 * - do_now: retoma imediatamente (pending)
 * - schedule: reagenda com data+hora (vira compromisso)
 * - delegate: reatribui a outro membro da BU
 * - eliminate: arquiva a atividade
 */
export type StaleReviewAction = "do_now" | "schedule" | "delegate" | "eliminate";

// ---------------------------------------------------------------------------
// Convidados/participantes de atividade (Fase 4 — modal rico)
// ---------------------------------------------------------------------------

/** Tipo de participante: interno (usuário da org) ou externo (por e-mail). */
export type ParticipantKind = "internal" | "external";

/**
 * Participante retornado pelo backend (ActivityResponse.participants).
 * `id` é o GUID do vínculo de participação (não do usuário).
 * internal: `userId` presente. external: `email` presente (+`displayName` opcional).
 */
export interface ActivityParticipant {
  readonly id?: string;
  readonly kind: ParticipantKind;
  readonly userId?: string;
  readonly email?: string;
  readonly displayName?: string;
}

/**
 * Input de participante enviado em POST/PUT (semântica de formulário —
 * substitui integralmente a lista existente no backend).
 */
export interface ParticipantInput {
  readonly kind: ParticipantKind;
  readonly userId?: string;
  readonly email?: string;
  readonly displayName?: string;
}

// ---------------------------------------------------------------------------
// Recorrência de atividades (BC-recurrence)
// ---------------------------------------------------------------------------

/**
 * Frequência de recorrência aceita pelo backend (RecurrenceRequest.frequency).
 * Nomes em inglês minúsculo, iguais ao contrato da API.
 */
export type RecurrenceFrequency =
  | "daily_business_days"
  | "weekly"
  | "biweekly"
  | "monthly"
  | "bimonthly"
  | "quarterly"
  | "semiannual"
  | "annual";

/** Dia da semana (nome em inglês minúsculo), usado em `byDay` para weekly/biweekly. */
export type RecurrenceWeekday =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

/**
 * Escopo de aplicação de uma edição/exclusão sobre uma atividade recorrente.
 * Enviado via query param `?scope=` em PUT/DELETE /v1/activities/{id}.
 * Ignorado pelo backend quando a atividade não pertence a uma série.
 */
export type RecurrenceScope = "this" | "this_and_following" | "all_series";

/**
 * Input de recorrência enviado em `POST /v1/activities` (campo `recurrence`).
 * `dtstart` é derivado pelo backend a partir de `dueDate`/`scheduledAt` — NÃO enviar.
 * `until` e `count` são mutuamente exclusivos.
 */
export interface RecurrenceInput {
  readonly frequency: RecurrenceFrequency;
  /** Intervalo entre ocorrências (>=1). Default 1 quando omitido. */
  readonly interval?: number;
  /** Dias da semana — apenas para weekly/biweekly. */
  readonly byDay?: readonly RecurrenceWeekday[];
  /** Data-limite (YYYY-MM-DD) — exclusivo com `count`. */
  readonly until?: string;
  /** Número de ocorrências — exclusivo com `until`. */
  readonly count?: number;
}

export interface ActivityItem {
  readonly id: string;
  readonly type: ActivityType;
  readonly title: string;
  readonly description?: string | null;
  /**
   * Nome da conta vinculada — OPCIONAL.
   * O backend de activity-management retorna apenas `accountId` (GUID).
   * O serviço de account-management não é consultado nesta requisição.
   * A UI exibe "—" quando ausente.
   */
  readonly accountName?: string;
  /**
   * Nome do responsável — OPCIONAL.
   * O backend retorna apenas `ownerId` (GUID).
   * O serviço de user-management não é consultado nesta requisição.
   * A UI exibe "—" quando ausente.
   */
  readonly ownerName?: string;
  /** Data de vencimento ou agendamento (ISO 8601). Usado para exibição. */
  readonly dueDate: string;
  readonly state: ActivityState;
  readonly priority: ActivityPriority;
  /**
   * GUID do responsável — preservado para permitir filtros de admin e
   * resolução de nomes via /v1/users.
   */
  readonly ownerId: string;
  /** GUID da BU — preservado para filtros client-side quando o backend não suporta buId param. */
  readonly buId?: string;
  /** GUID da oportunidade vinculada — opcional. */
  readonly opportunityId?: string | null;
  /** GUID da conta vinculada — opcional. */
  readonly accountId?: string | null;
  /** GUID do parceiro vinculado — opcional (Fase 5). */
  readonly partnerId?: string | null;
  /** Status bruto do backend — preservado para formulário de edição. */
  readonly status: ActivityStatus;

  // ── Campos adicionados em TASK-29 (task-management) ──────────────────────

  /**
   * Natureza derivada do agendamento.
   * "task" = só data; "appointment" = data+hora.
   */
  readonly nature?: ActivityNature;
  /** Quadrante de Eisenhower derivado pelo backend (não editável). */
  readonly quadrant?: ActivityQuadrant;
  /** Urgência definida pelo usuário (eixo X da Matriz de Eisenhower). */
  readonly isUrgent?: boolean;
  /** Importância definida pelo usuário (eixo Y da Matriz de Eisenhower). */
  readonly isImportant?: boolean;
  /**
   * Urgência auto-sugerida pelo backend com base no prazo iminente.
   * Pré-marca o toggle de urgência no form (sobrescrevível pelo usuário).
   */
  readonly suggestedUrgent?: boolean;
  /**
   * Número de vezes que esta tarefa foi migrada automaticamente pelo job.
   * Exibido como badge na seção "migradas" do Meu Dia.
   */
  readonly migrationCount?: number;
  /**
   * Indica se a tarefa está "encalhada" (migrationCount >= threshold da BU/tenant
   * e status aberto). Quando true, aparece na seção staleReview do Meu Dia.
   */
  readonly isStale?: boolean;
  /**
   * Data de pin para o "Meu Dia" (ISO 8601 data-only, null = não fixada).
   */
  readonly pinnedDate?: string | null;
  /**
   * Ação sugerida pelo servidor para revisão de encalhada.
   * Presente apenas em items da seção staleReview de GET /me/day.
   */
  readonly suggestedAction?: StaleReviewAction;
  /**
   * Data+hora agendada (ISO 8601 UTC) — presente apenas quando nature === "appointment".
   */
  readonly scheduledAt?: string | null;

  // ── Campos de recorrência (BC-recurrence) ─────────────────────────────────

  /**
   * GUID da regra de recorrência à qual esta atividade pertence.
   * `null` quando a atividade não faz parte de uma série.
   */
  readonly recurrenceRuleId?: string | null;
  /**
   * Data-âncora (YYYY-MM-DD) desta ocorrência dentro da série — usada pelo
   * backend para localizar a ocorrência ao aplicar escopos this/this_and_following.
   */
  readonly recurrenceAnchor?: string | null;
  /**
   * Indica se esta ocorrência foi editada individualmente (exceção da série),
   * divergindo do padrão da regra de recorrência.
   */
  readonly isSeriesException?: boolean;

  // ── Notas rich-text e convidados (Fase 4 — modal rico) ────────────────────

  /**
   * HTML das notas, já sanitizado pelo backend. Quando ausente, a atividade só
   * possui `description` texto-plano (compatibilidade com registros antigos).
   */
  readonly descriptionHtml?: string | null;
  /** Convidados/participantes vinculados à atividade (internos e externos). */
  readonly participants?: readonly ActivityParticipant[];

  // ── Lembretes de atividade (Fase 6) ────────────────────────────────────────

  /**
   * Antecedência do lembrete em minutos. `null`/ausente = usa o default do
   * tenant (`TaskSettings.reminderMinutesBeforeDefault`); `0` = lembrete
   * desativado explicitamente para esta atividade; `N>0` = antecedência.
   */
  readonly reminderMinutesBefore?: number | null;
}

export interface ActivityFiltersState {
  readonly stateFilter: ActivityState | null;
  readonly ownerFilter: string | null;
  readonly buFilter: string | null;
}

// ---------------------------------------------------------------------------
// Tipos de input para operações de escrita
// ---------------------------------------------------------------------------

/** Prioridade no formato do backend (expandida de "med" → "medium"). */
export type BackendPriority = "low" | "medium" | "high";

/** Mapeamento de prioridade portal → backend. */
export function mapPriorityToBackend(p: ActivityPriority | string): BackendPriority {
  if (p === "med") return "medium";
  if (p === "high") return "high";
  return "low";
}

/** Input para criação de atividade (POST /v1/activities). */
export interface CreateActivityInput {
  readonly type: ActivityType;
  readonly title: string;
  /**
   * Data+hora de vencimento (ISO 8601 UTC).
   * Mapeado para `scheduledAt` no body da API (cria compromisso — nature=appointment).
   * Mantido para compatibilidade; prefira `dueDate` ou `scheduledAt` explícitos.
   */
  readonly dueAt?: string;
  /**
   * Data de vencimento para tarefa (YYYY-MM-DD).
   * Exclusivo com scheduledAt. Quando presente, backend deriva nature=task.
   */
  readonly dueDate?: string;
  /**
   * Data+hora agendada para compromisso (ISO 8601 UTC).
   * Exclusivo com dueDate. Quando presente, backend deriva nature=appointment.
   */
  readonly scheduledAt?: string;
  readonly ownerId: string;
  readonly priority?: ActivityPriority;
  /** Urgência (eixo X do Eisenhower). Sobrescreve suggestedUrgent. */
  readonly isUrgent?: boolean;
  /** Importância (eixo Y do Eisenhower). */
  readonly isImportant?: boolean;
  readonly description?: string;
  /**
   * HTML das notas (editor rich-text). Quando presente, o backend sanitiza e
   * deriva `description` (texto-plano) a partir dele — não envie `description`
   * junto.
   */
  readonly descriptionHtml?: string;
  readonly opportunityId?: string;
  readonly accountId?: string;
  /** GUID do parceiro vinculado — opcional (Fase 5). */
  readonly partnerId?: string;
  /**
   * Padrão de recorrência opcional. Quando presente, o backend materializa a
   * série a partir do `dueDate`/`scheduledAt` desta atividade (dtstart derivado
   * — não enviado explicitamente).
   */
  readonly recurrence?: RecurrenceInput;
  /**
   * Convidados/participantes da atividade. Substitui integralmente a lista no
   * backend (semântica de formulário).
   */
  readonly participants?: readonly ParticipantInput[];
  /**
   * Antecedência do lembrete em minutos. Quando ausente, o backend usa o
   * default do tenant; `0` desativa o lembrete explicitamente para esta
   * atividade.
   */
  readonly reminderMinutesBefore?: number;
}

/** Input para atualização de atividade (PUT /v1/activities/{id}). */
export interface UpdateActivityInput {
  readonly type: ActivityType;
  readonly title: string;
  /**
   * Data+hora de vencimento (ISO 8601 UTC).
   * Mantido para compatibilidade; prefira `dueDate` ou `scheduledAt`.
   */
  readonly dueAt?: string;
  /** Data de vencimento para tarefa (YYYY-MM-DD). */
  readonly dueDate?: string;
  /** Data+hora agendada para compromisso (ISO 8601 UTC). */
  readonly scheduledAt?: string;
  readonly priority?: ActivityPriority;
  readonly isUrgent?: boolean;
  readonly isImportant?: boolean;
  readonly description?: string;
  /**
   * HTML das notas (editor rich-text). Quando presente, o backend sanitiza e
   * deriva `description` (texto-plano) a partir dele — não envie `description`
   * junto.
   */
  readonly descriptionHtml?: string;
  readonly opportunityId?: string;
  readonly accountId?: string;
  /** GUID do parceiro vinculado — opcional (Fase 5). */
  readonly partnerId?: string;
  /**
   * Convidados/participantes da atividade. Substitui integralmente a lista no
   * backend (semântica de formulário).
   */
  readonly participants?: readonly ParticipantInput[];
  /**
   * Antecedência do lembrete em minutos. A CHAVE AUSENTE preserva o lembrete
   * atual da atividade (o backend distingue "não informado" de "informado com
   * null" — LDG-0090); `null` explícito volta ao default do tenant e `0`
   * desativa o lembrete para esta atividade.
   */
  readonly reminderMinutesBefore?: number | null;
}

// ── Tipos específicos de Fase 5 (próxima atividade) ────────────────────────

/**
 * Sugestão de próxima atividade retornada por PATCH /activities/{id}/complete
 * quando a atividade concluída está vinculada a uma oportunidade aberta.
 */
export interface NextActivitySuggestion {
  readonly opportunityId: string;
  readonly accountId?: string | null;
}

// ── Tipos específicos de task-management (TASK-29) ────────────────────────

/**
 * Resposta seccionada de GET /api/v1/activities/me/day (REQ-14).
 * Ordenação dentro de cada seção: Q1→Q4 no servidor (REQ-11).
 */
export interface MyDayResponse {
  /** Atividades encalhadas pendentes de revisão (migrationCount >= threshold). */
  readonly staleReview: readonly ActivityItem[];
  /**
   * Atividades migradas automaticamente (ainda não revisadas, migrationCount ≥ 1).
   * Cada item traz migrationCount para exibição do badge de contador.
   */
  readonly migrated: readonly ActivityItem[];
  /** Atividades do dia atual (due_date = hoje). */
  readonly today: readonly ActivityItem[];
  /** Compromissos do dia atual (scheduled_at com hora = hoje). */
  readonly appointments: readonly ActivityItem[];
  /**
   * Atividades concluídas hoje (bucket novo — Item 7 / quickwins de UX).
   * Exibidas em seção colapsável recolhida por padrão.
   * Opcional para compatibilidade com respostas/mocks antigos que não o incluem.
   */
  readonly completedToday?: readonly ActivityItem[];
}

/**
 * Configurações de tarefas por tenant (GET/PUT /api/v1/task-settings).
 * Visível e editável apenas por Tenant Admin (REQ-19).
 */
export interface TaskSettings {
  /** Número de migrações automáticas antes de marcar como encalhada (padrão: 3). */
  readonly migrationThreshold: number;
  /**
   * Modo de calendário para contagem de dias úteis.
   * "business_days" = pula sáb/dom (padrão); "calendar_days" = todos os dias.
   */
  readonly calendarMode: "business_days" | "calendar_days";
  /**
   * Habilita a sugestão automática de urgência quando o prazo é hoje/amanhã.
   */
  readonly urgencyAutoSuggest: boolean;
  /**
   * Antecedência padrão do lembrete de atividade, em minutos (padrão: 15).
   * `0` desativa o lembrete por padrão para novas atividades do tenant.
   */
  readonly reminderMinutesBeforeDefault: number;
}

/** Resultado de PATCH /{id}/prioritize. */
export interface PrioritizeResult {
  readonly activityId: string;
  readonly quadrant: ActivityQuadrant;
  readonly priority: ActivityPriority;
}

/** Input para POST /{id}/review (revisão de encalhada). */
export interface ReviewStaleInput {
  readonly action: StaleReviewAction;
  /** Obrigatório quando action === "schedule". ISO 8601 UTC. */
  readonly scheduledAt?: string;
  /** Obrigatório quando action === "delegate". GUID do novo responsável. */
  readonly assigneeId?: string;
}
